import { c as _c } from "react/compiler-runtime";
import { postHostedUserDocumentsByIdProcessMutation } from '@onefootprint/fetch-hosted';
import { useRequestError } from '@onefootprint/fetch-hosted/use-request-error';
import type { DocumentResponse } from '@onefootprint/request-types';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import type { IdDocImageSide } from '@/idv2/features/collect-document/types';
import { getLogger, trackAction } from '@/idv2/utils/logger';
import { SLOW_CONNECTION_MESSAGE_TIMEOUT } from '../../../constants';
import useSubmitDoc from '../../../hooks/use-submit-doc';
import useUploadVideo from '../../../hooks/use-upload-video';
import { IdDocImageUploadError } from '../../../types';
import getBackendErrorMessage from '../../../utils/get-backend-error-message';
import getRejectionCodes from '../../../utils/get-rejection-codes';
import useIdDocMachine from '../../hooks/use-id-doc-machine';
import ProcessingView from './processing-view';
const {
  logWarn
} = getLogger({
  name: 'idv2',
  location: 'processing'
});
const Processing = () => {
  const $ = _c(63);
  const {
    getErrorMessage,
    getErrorCode
  } = useRequestError();
  const [state, dispatch] = useIdDocMachine();
  const submitDocMutation = useSubmitDoc();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = postHostedUserDocumentsByIdProcessMutation();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const processDocMutation = useMutation(t0);
  const uploadVideo = useUploadVideo();
  const [mode, setMode] = useState("loading");
  const [nextSide, setNextSide] = useState();
  const [retryLimitExceeded, setRetryLimitExceeded] = useState(false);
  const [showSlowConnectionMessage, setShowSlowConnectionMessage] = useState(false);
  const [isMissingRequirements, setIsMissingRequirements] = useState(false);
  const [step, setStep] = useState("upload");
  const slowConnectionTimer = useRef(null);
  const {
    idDoc: t1,
    image,
    authToken,
    currSide,
    id,
    hasBadConnectivity
  } = state.context;
  const {
    type,
    country
  } = t1;
  let t2;
  if ($[1] !== country || $[2] !== currSide || $[3] !== dispatch || $[4] !== id || $[5] !== state.context.currSide || $[6] !== type) {
    t2 = data => {
      const {
        errorDetails,
        nextSideToCollect,
        isRetryLimitExceeded
      } = data;
      const rejectionCodes = getRejectionCodes(data);
      const rejectionContext = {
        rejectionCodes,
        docType: type,
        country,
        side: currSide
      };
      if (isRetryLimitExceeded) {
        logWarn(`Image upload retry limit exceeded. Side: ${currSide}, doc id: ${id}`, undefined, rejectionContext);
        trackAction(`document-processing-${currSide}:retry-limit-exceeded`, rejectionContext);
        setRetryLimitExceeded(true);
        return;
      }
      if (nextSideToCollect === state.context.currSide) {
        logWarn("Document rejected", undefined, rejectionContext);
        trackAction(`document-processing-${currSide}:rejected`, rejectionContext);
        dispatch({
          type: "processingErrored",
          payload: {
            errors: errorDetails.map(_temp)
          }
        });
        return;
      }
      if (nextSideToCollect) {
        setNextSide(nextSideToCollect as IdDocImageSide);
      }
      setMode("success");
      trackAction(`document-processing-${currSide}:completed`, rejectionContext);
    };
    $[1] = country;
    $[2] = currSide;
    $[3] = dispatch;
    $[4] = id;
    $[5] = state.context.currSide;
    $[6] = type;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const handleProcessDocSuccess = t2;
  let t3;
  if ($[8] !== currSide) {
    t3 = () => {
      trackAction(`document-processing-${currSide}:started`);
    };
    $[8] = currSide;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  useEffect(t3, t4);
  let t5;
  if ($[11] !== dispatch || $[12] !== getErrorMessage) {
    t5 = err_0 => {
      const backendMessage = getBackendErrorMessage(err_0);
      dispatch({
        type: "processingErrored",
        payload: {
          errors: [backendMessage ? {
            errorType: "custom_error",
            errorInfo: backendMessage
          } : {
            errorType: IdDocImageUploadError.networkError,
            errorInfo: getErrorMessage(err_0)
          }]
        }
      });
    };
    $[11] = dispatch;
    $[12] = getErrorMessage;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const handleError = t5;
  let t6;
  if ($[14] !== getErrorMessage || $[15] !== handleError || $[16] !== id) {
    t6 = err_1 => {
      handleError(err_1);
      logWarn(`Error while processing id-doc image ${id}: ${getErrorMessage(err_1)}`, err_1);
    };
    $[14] = getErrorMessage;
    $[15] = handleError;
    $[16] = id;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const handleProcessDocError = t6;
  let t7;
  if ($[18] !== currSide || $[19] !== getErrorCode || $[20] !== getErrorMessage || $[21] !== handleError || $[22] !== id) {
    t7 = err_2 => {
      const errorMessage = getErrorMessage(err_2);
      const errorCode = getErrorCode(err_2);
      if (errorCode === "E109") {
        setNextSide(undefined);
        setMode("success");
        return;
      }
      handleError(err_2);
      logWarn(`Id-doc image submit failed on phone flow. Side: ${currSide}, upload session id: ${id}. Error: ${errorMessage}`, err_2);
    };
    $[18] = currSide;
    $[19] = getErrorCode;
    $[20] = getErrorMessage;
    $[21] = handleError;
    $[22] = id;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const handleSubmitDocError = t7;
  let t8;
  if ($[24] !== authToken || $[25] !== handleProcessDocError || $[26] !== handleProcessDocSuccess || $[27] !== hasBadConnectivity || $[28] !== id || $[29] !== image || $[30] !== processDocMutation || $[31] !== uploadVideo) {
    t8 = async () => {
      if (!id) {
        return;
      }
      if (image?.videoFile && !hasBadConnectivity) {
        await uploadVideo({
          documentId: id,
          documentKind: "identity",
          videoFile: image.videoFile,
          authToken
        });
      }
      setStep("analyze");
      ;
      try {
        const response = await processDocMutation.mutateAsync({
          path: {
            id
          },
          headers: {
            "X-Fp-Authorization": authToken
          }
        });
        handleProcessDocSuccess(response);
      } catch (t9) {
        const error = t9;
        handleProcessDocError(error);
      }
    };
    $[24] = authToken;
    $[25] = handleProcessDocError;
    $[26] = handleProcessDocSuccess;
    $[27] = hasBadConnectivity;
    $[28] = id;
    $[29] = image;
    $[30] = processDocMutation;
    $[31] = uploadVideo;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  const handleSubmitDocSuccess = t8;
  let t9;
  if ($[33] !== authToken || $[34] !== country || $[35] !== currSide || $[36] !== handleSubmitDocError || $[37] !== handleSubmitDocSuccess || $[38] !== id || $[39] !== image || $[40] !== submitDocMutation || $[41] !== type) {
    t9 = () => {
      if (!image || !authToken || !type || !country || !currSide || !id) {
        setIsMissingRequirements(true);
        const error_0 = `Mobile web flow - id-doc image could not be processed due to missing requirements. Requirements - image: ${image ? "OK" : "undefined"}, auth token: ${authToken ? "OK" : "undefined"}, doc type: ${type ? "OK" : "undefined"}, country: ${country ? "OK" : "undefined"}, current side: ${currSide ? "OK" : "undefined"}, id: ${id || "undefined"}`;
        logWarn(error_0);
        return;
      }
      if (!slowConnectionTimer.current) {
        slowConnectionTimer.current = setTimeout(() => {
          if (submitDocMutation.isSuccess) {
            return;
          }
          setShowSlowConnectionMessage(true);
        }, SLOW_CONNECTION_MESSAGE_TIMEOUT);
      }
      const {
        imageFile,
        extraCompressed,
        captureKind,
        forcedUpload
      } = image;
      submitDocMutation.mutate({
        body: {
          fileData: imageFile
        },
        path: {
          id,
          side: currSide
        },
        headers: {
          "X-Fp-Authorization": authToken,
          "X-Fp-Is-Extra-Compressed": String(extraCompressed),
          "X-Fp-Is-Manual": String(captureKind === "manual"),
          "X-Fp-Is-App-Clip": "false",
          "X-Fp-Is-Instant-App": "false",
          "X-Fp-Is-Upload": String(captureKind === "upload"),
          "X-Fp-Is-Forced-Upload": String(forcedUpload)
        }
      }, {
        onSuccess: handleSubmitDocSuccess,
        onError: handleSubmitDocError,
        onSettled: () => {
          if (slowConnectionTimer.current) {
            clearTimeout(slowConnectionTimer.current);
            setShowSlowConnectionMessage(false);
          }
        }
      });
    };
    $[33] = authToken;
    $[34] = country;
    $[35] = currSide;
    $[36] = handleSubmitDocError;
    $[37] = handleSubmitDocSuccess;
    $[38] = id;
    $[39] = image;
    $[40] = submitDocMutation;
    $[41] = type;
    $[42] = t9;
  } else {
    t9 = $[42];
  }
  let t10;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = [];
    $[43] = t10;
  } else {
    t10 = $[43];
  }
  useEffect(t9, t10);
  let t11;
  if ($[44] !== dispatch) {
    t11 = () => dispatch({
      type: "retryLimitExceeded"
    });
    $[44] = dispatch;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  const handleRetryLimitExceeded = t11;
  let t12;
  if ($[46] !== dispatch) {
    t12 = () => dispatch({
      type: "processingSucceeded",
      payload: {
        nextSideToCollect: undefined
      }
    });
    $[46] = dispatch;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  const onSuccessComplete = t12;
  let t13;
  if ($[48] !== dispatch || $[49] !== nextSide) {
    t13 = () => dispatch({
      type: "processingSucceeded",
      payload: {
        nextSideToCollect: nextSide
      }
    });
    $[48] = dispatch;
    $[49] = nextSide;
    $[50] = t13;
  } else {
    t13 = $[50];
  }
  const onNextSideComplete = t13;
  let t14;
  if ($[51] !== currSide || $[52] !== handleRetryLimitExceeded || $[53] !== isMissingRequirements || $[54] !== mode || $[55] !== nextSide || $[56] !== onNextSideComplete || $[57] !== onSuccessComplete || $[58] !== retryLimitExceeded || $[59] !== showSlowConnectionMessage || $[60] !== step || $[61] !== type) {
    t14 = <ProcessingView mode={mode} step={step} nextSide={nextSide} showSlowConnectionMessage={showSlowConnectionMessage} retryLimitExceeded={retryLimitExceeded} isMissingRequirements={isMissingRequirements} docType={type} currSide={currSide} onRetryLimitExceeded={handleRetryLimitExceeded} onSuccessComplete={onSuccessComplete} onNextSideComplete={onNextSideComplete} />;
    $[51] = currSide;
    $[52] = handleRetryLimitExceeded;
    $[53] = isMissingRequirements;
    $[54] = mode;
    $[55] = nextSide;
    $[56] = onNextSideComplete;
    $[57] = onSuccessComplete;
    $[58] = retryLimitExceeded;
    $[59] = showSlowConnectionMessage;
    $[60] = step;
    $[61] = type;
    $[62] = t14;
  } else {
    t14 = $[62];
  }
  return t14;
};
export default Processing;
function _temp(err) {
  return {
    errorType: err.code ?? "custom_error",
    errorInfo: err.message
  };
}